import { CircularProgress, Typography } from '@mui/material'
import React, { useState, type FunctionComponent } from 'react'
import { useAvailableIntegrationsQuery } from '../../graphql/generated/generated'
import { trackAnalyticEvent, ANALYTICS_LOCATIONS, filterIntegrations } from '../../utils'
import {
	AvailableIntegration,
	CardGrid,
	IntegrationsFilters,
} from '../'
import { AvailableIntegrationsWrapper } from './available-integrations.style'

export const AvailableIntegrations: FunctionComponent = () => {
	const { data, isLoading, isError } = useAvailableIntegrationsQuery()
	const [ searchValue, setSearchValue ] = useState<string | null>( null )
	const [ pillValue, setPillValue ] = useState<string>( 'All' )
	const integrations = data?.integrations ?? []

	const integrationsFiltered = filterIntegrations( integrations )
	const integrationsSearched = searchValue ? integrationsFiltered.filter( ( integration ) => integration.name.toLowerCase().includes( searchValue.toLowerCase() ) ) : integrationsFiltered
	const aemp20Integrations = integrationsSearched.filter( ( integration ) => integration.isAEMPV2 )
	const otherIntegrations = integrationsSearched.filter( ( integration ) => !integration.isAEMPV2 )

	const getIntegrationsToShow = () => {
		switch ( pillValue ) {
		case 'AEMP 2.0':
			return aemp20Integrations
		case 'Other':
			return otherIntegrations
		default:
			return integrationsSearched
		}
	}

	const shownIntegrations = getIntegrationsToShow()

	const sortedIntegrations = shownIntegrations.sort( ( a, b ) => a.name.localeCompare( b.name ) )

	return (
		<AvailableIntegrationsWrapper>
			<Typography variant={'h3'}>Available Integrations</Typography>
			<IntegrationsFilters
				onSearchChange={setSearchValue}
				onSearchStart={() => {
					trackAnalyticEvent(
						ANALYTICS_LOCATIONS.AVAILABLE_INTEGRATIONS,
						'Use',
						'Search Bar',
					)
				}}
				onPillClick={( newPillValue: string ) => {
					trackAnalyticEvent(
						ANALYTICS_LOCATIONS.AVAILABLE_INTEGRATIONS,
						'Click',
						`${newPillValue} Pill/Quick Filter`,
					)

					setPillValue( newPillValue )
				}}
				pills={[
					{
						label: 'All',
						count: integrationsSearched.length,
						selected: pillValue === 'All',
					},
					{
						label: 'AEMP 2.0',
						count: aemp20Integrations.length,
						selected: pillValue === 'AEMP 2.0',
					},
					{
						label: 'Other',
						count: otherIntegrations.length,
						selected: pillValue === 'Other',
					},
				]}
			/>
			{ isLoading && <CircularProgress /> }
			{ isError && (
				<Typography variant={'body2'} color={'error'}>
					Failed to load available integrations. Please try reloading the page. If the problem persists, please contact support.
				</Typography>
			) }
			<CardGrid>
				{ sortedIntegrations.map( ( availableIntegration ) => (
					<AvailableIntegration
						key={availableIntegration.id}
						availableIntegration={availableIntegration}
					/>
				) )}
			</CardGrid>
		</AvailableIntegrationsWrapper>
	)
}
